import {
  AuthDesktopCard,
  AuthDesktopHeader,
  AuthDesktopLayout,
  AuthScreenContent,
  AuthScreenFooter,
  AuthScreenHeader,
  AuthScreenLayout,
  AuthScreenSpacer,
  authScreenStyles,
} from "@/components/auth/auth-screen-layout";
import { ThemedText } from "@/components/themed-text";
import { BrandButton } from "@/components/ui/brand-button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandColors } from "@/constants/theme";
import { useDesktopLayout } from "@/hooks/use-desktop-layout";
import authService from "@/lib/auth";
import { showToast } from "@/lib/toast";
import { zodResolver } from "@hookform/resolvers/zod";
import Ionicons from "@react-native-vector-icons/ionicons";
import { Link, router } from "expo-router";
import React from "react";
import { Controller, useForm } from "react-hook-form";
import { StyleSheet, TouchableOpacity, View } from "react-native";
import { HelperText } from "react-native-paper";
import { z } from "zod";

const forgotPasswordSchema = z.object({
  email: z.string().email("Invalid email address"),
});

type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>;

export default function ForgotPasswordScreen() {
  const desktop = useDesktopLayout();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<ForgotPasswordFormData>({
    resolver: zodResolver(forgotPasswordSchema),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      await authService.forgotPassword(data.email);
      showToast.success(
        "Password reset instructions have been sent to your email",
      );
      reset();
    } catch (error: any) {
      showToast.error(
        error.response?.data?.message || "Failed to send reset instructions",
      );
    }
  };

  const handleBack = () => {
    if (router.canGoBack()) {
      router.back();
    } else {
      router.replace("/(auth)");
    }
  };

  const title = "Forgot Password?";
  const subtitle =
    "Enter your email address and we'll send you instructions to reset your password";

  const formFields = (
    <View
      style={[
        desktop ? authScreenStyles.desktopForm : authScreenStyles.form,
        desktop && styles.desktopForm,
      ]}
    >
      <View style={authScreenStyles.field}>
        <Label label={desktop ? "Email address" : "Email"} />
        <Controller
          control={control}
          name="email"
          render={({ field: { onChange, onBlur, value } }) => (
            <Input
              placeholder={desktop ? "you@example.com" : "Enter your email"}
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              keyboardType="email-address"
              autoCapitalize="none"
              autoComplete="email"
              error={!!errors.email}
              style={[
                authScreenStyles.input,
                desktop && authScreenStyles.desktopInput,
              ]}
              outlineStyle={
                desktop
                  ? authScreenStyles.desktopInputOutline
                  : authScreenStyles.inputOutline
              }
            />
          )}
        />
        {errors.email && (
          <HelperText type="error" visible={!!errors.email}>
            {errors.email?.message}
          </HelperText>
        )}
      </View>
    </View>
  );

  const submitButton = (
    <BrandButton
      variant="primary"
      loading={isSubmitting}
      onPress={handleSubmit(onSubmit)}
      style={
        desktop
          ? authScreenStyles.desktopContinueButton
          : authScreenStyles.continueButton
      }
      labelStyle={
        desktop
          ? authScreenStyles.desktopContinueButtonLabel
          : authScreenStyles.continueButtonLabel
      }
      contentStyle={authScreenStyles.continueButtonContent}
      icon={() => (
        <Ionicons
          name="chevron-forward"
          size={desktop ? 14 : 18}
          color={BrandColors.white}
        />
      )}
    >
      {isSubmitting ? "Sending..." : "Send Reset Instructions"}
    </BrandButton>
  );

  const signInFooter = (
    <AuthScreenFooter>
      <ThemedText style={authScreenStyles.footerText} type="small">
        Remember your password?{" "}
      </ThemedText>
      <Link href="/(auth)" asChild>
        <TouchableOpacity>
          <ThemedText style={authScreenStyles.link} type="smallSemiBold">
            Sign In
          </ThemedText>
        </TouchableOpacity>
      </Link>
    </AuthScreenFooter>
  );

  if (desktop) {
    return (
      <AuthDesktopLayout onBack={handleBack}>
        <AuthDesktopHeader title={title} subtitle={subtitle} />
        <AuthDesktopCard>
          {formFields}
          {submitButton}
          {signInFooter}
        </AuthDesktopCard>
      </AuthDesktopLayout>
    );
  }

  return (
    <AuthScreenLayout onBack={handleBack} keyboardInset>
      <AuthScreenContent>
        <AuthScreenHeader title={title} subtitle={subtitle} />

        {formFields}
      </AuthScreenContent>

      <AuthScreenSpacer />

      <AuthScreenContent>{submitButton}</AuthScreenContent>

      {signInFooter}
    </AuthScreenLayout>
  );
}

const styles = StyleSheet.create({
  desktopForm: {
    marginBottom: 20,
  },
});
